import { Tab } from '@headlessui/react'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'
import { api } from '../../services/api'
import AIImageUploadTab from './AIImageUploadTab'
import VideoUploadTab from './VideoUploadTab'
import ImageGallery from './ImageGallery'
import { useHasFeature, invalidateSubscriptionCache } from '../../hooks/useSubscription'
import { ErrorBoundary } from '../shared/ErrorBoundary'

/**
 * CreateStoriesTabs Component
 *
 * Tab navigation for Create Stories page:
 * 1. Upload New Content (AI Image + Video)
 * 2. Reuse Existing Content (Image Gallery)
 *
 * Phase 3: Now uses account-level feature checks for video upload gating
 */
function CreateStoriesTabs({ accountId }) {
  const { t } = useTranslation('upload')
  // Phase 3: Pass accountId to check account-specific video upload feature
  const { hasFeature: hasVideoUpload, loading } = useHasFeature('video_upload', accountId)

  // Handle image reuse selection
  const handleImageReuse = async (image) => {
    try {
      // Show loading toast
      const loadingToast = toast.loading('Generating fresh caption for reused image...', {
        position: 'top-right'
      })

      // Call reuse endpoint
      // Backend expects target_account_id and source_account_id
      // For reuse within same account, both are the same
      const result = await api.reuseImage(image.image_id, {
        target_account_id: accountId,
        source_account_id: accountId,
        sentiment: image.sentiment,
      })

      // Update toast to success
      toast.update(loadingToast, {
        render: 'Caption generation started! Check the Approval Queue.',
        type: 'success',
        isLoading: false,
        autoClose: 5000,
        closeButton: true,
      })

      // Invalidate subscription cache to force immediate quota refresh
      invalidateSubscriptionCache(accountId).catch(err =>
        console.warn('Failed to refresh subscription cache:', err)
      )

      // Optional: Navigate to approval queue after 2 seconds
      setTimeout(() => {
        window.location.href = '/approval'
      }, 2000)

    } catch (error) {
      console.error('Failed to reuse image:', error)
      toast.error(error.message || 'Failed to reuse image. Please try again.', {
        position: 'top-right',
        autoClose: 5000,
      })
    }
  }

  return (
    <Tab.Group>
      <Tab.List className="flex space-x-1 bg-gray-50 p-1 rounded-lg border-b border-gray-200">
        {/* AI Image Upload Tab */}
        <Tab
          className={({ selected }) =>
            `flex-1 py-3 px-4 text-sm font-medium rounded-md transition-all focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 ${
              selected
                ? 'bg-white text-gray-900 shadow-sm border-b-2 border-purple-600'
                : 'text-gray-600 hover:text-gray-900 hover:bg-white/50'
            }`
          }
        >
          {({ selected }) => (
            <div className="flex items-center justify-center space-x-2">
              {/* Upload Icon */}
              <svg
                className={`h-5 w-5 ${selected ? 'text-purple-600' : 'text-gray-400'}`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                />
              </svg>
              <span>Upload New</span>
              {/* AI-Powered Badge */}
              <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gradient-to-r from-purple-600 to-pink-500 text-white">
                AI
              </span>
            </div>
          )}
        </Tab>

        {/* Video Upload Tab - Feature Gated */}
        <Tab
          disabled={!hasVideoUpload}
          className={({ selected }) =>
            `flex-1 py-3 px-4 text-sm font-medium rounded-md transition-all focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 ${
              !hasVideoUpload
                ? 'bg-gray-100 text-gray-400 cursor-not-allowed opacity-60'
                : selected
                ? 'bg-white text-gray-900 shadow-sm border-b-2 border-purple-600'
                : 'text-gray-600 hover:text-gray-900 hover:bg-white/50'
            }`
          }
        >
          {({ selected }) => (
            <div className="flex items-center justify-center space-x-2">
              {/* Film Icon */}
              <svg
                className={`h-5 w-5 ${selected ? 'text-purple-600' : 'text-gray-400'}`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M7 4v16M17 4v16M3 8h4m10 0h4M3 12h18M3 16h4m10 0h4M4 20h16a1 1 0 001-1V5a1 1 0 00-1-1H4a1 1 0 00-1 1v14a1 1 0 001 1z"
                />
              </svg>
              <span>Video{!hasVideoUpload && ' 🔒'}</span>
            </div>
          )}
        </Tab>

        {/* Reuse Existing Content Tab */}
        <Tab
          className={({ selected }) =>
            `flex-1 py-3 px-4 text-sm font-medium rounded-md transition-all focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 ${
              selected
                ? 'bg-white text-gray-900 shadow-sm border-b-2 border-purple-600'
                : 'text-gray-600 hover:text-gray-900 hover:bg-white/50'
            }`
          }
        >
          {({ selected }) => (
            <div className="flex items-center justify-center space-x-2">
              {/* Refresh/Reuse Icon */}
              <svg
                className={`h-5 w-5 ${selected ? 'text-purple-600' : 'text-gray-400'}`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                />
              </svg>
              <span>Reuse Existing</span>
            </div>
          )}
        </Tab>
      </Tab.List>

      {/* Feature Locked Banner for Video */}
      {!loading && !hasVideoUpload && (
        <div className="mt-4 rounded-lg bg-gradient-to-r from-purple-50 to-pink-50 border border-purple-200 p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <svg className="h-6 w-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
              <div>
                <p className="text-sm font-medium text-gray-900">
                  {t('tabs.featureGate.title')}
                </p>
                <p className="text-xs text-gray-600 mt-1">
                  {t('tabs.featureGate.description')}
                </p>
              </div>
            </div>
            <a
              href="/subscription"
              className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-purple-600 to-pink-600 text-white text-sm font-medium rounded-lg hover:from-purple-700 hover:to-pink-700 transition-all shadow-sm"
            >
              {t('tabs.featureGate.button')}
            </a>
          </div>
        </div>
      )}

      <Tab.Panels className="mt-6">
        {/* Upload New Content Panel */}
        <Tab.Panel className="focus:outline-none">
          <AIImageUploadTab />
        </Tab.Panel>

        {/* Video Upload Panel */}
        <Tab.Panel className="focus:outline-none">
          <VideoUploadTab />
        </Tab.Panel>

        {/* Reuse Existing Content Panel */}
        <Tab.Panel className="focus:outline-none">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="mb-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-2">
                Reuse Your Best Content
              </h2>
              <p className="text-sm text-gray-600">
                Select a previously posted image to create a new story with a fresh AI-generated caption.
                Perfect for A/B testing different captions for the same image.
              </p>
            </div>

            <ErrorBoundary
              errorTitle="Failed to load image gallery"
              errorMessage="We couldn't load your archived images. Please try refreshing the page or contact support if the problem persists."
              onReset={() => window.location.reload()}
            >
              <ImageGallery
                accountId={accountId}
                onImageSelect={handleImageReuse}
                selectionMode="single"
              />
            </ErrorBoundary>
          </div>
        </Tab.Panel>
      </Tab.Panels>
    </Tab.Group>
  )
}

export default CreateStoriesTabs
